/**
 * 65.22a (the owner: "bullets / smoke / fire doesn't actually come out of hmg barrel"): a shot fired from a mounted bullet
 * gun — the emplaced HMG (rendering/hmgModel.ts), a vehicle's cupola or pintle (rendering/vehicleModels.ts) — flashes,
 * smokes and starts its tracer at the drawn gun's muzzle, laid as the gun is drawn (its traverse and cradle), not at the
 * firer's own carried rifle, which is where every shot's effects started. Presentation only: the sim's round starts where
 * the gun puts it, and the tracer blends from the drawn muzzle onto it as any round's does.
 */
import type * as THREE from 'three';
import type { GameState } from '../types';

/**
 * The drawn muzzle a unit's shot comes out of when it works a mounted bullet gun (its seat's `guns`), from the drawn body
 * it sits in (`muzzleOf`: the host's model of that body, its muzzle anchor); null when it fires its own gun — on foot, or
 * from a seat with no bullet gun — or the body is not drawn.
 */
export function mountedGunMuzzle(state: Pick<GameState, 'players' | 'structures'>, unitId: number, muzzleOf: (bodyId: number) => THREE.Object3D | null | undefined): THREE.Object3D | null {
  const mount = state.players.find(p => p.id === unitId)?.mount;
  if (!mount) return null;
  const seat = state.structures.find(s => s.id === mount.bodyId)?.seats?.[mount.seat];
  if (!seat?.guns?.length) return null;
  return muzzleOf(mount.bodyId) ?? null;
}
